// Providers + head plumbing — replaces eb-ui `src/index.tsx` (client bootstrap)
// + `BspAppRoot` (site-structure-bsp, restored as <BspProvider> below). Mounted
// app-wide (not per-page) since they carry app-level state (locale catalog,
// react-query, follow client).
//
// ★ FONT OVERRIDE (verified LIVE per the app config `_notes`): the real page
// loads FOUNDERS GROTESK (300-700), NOT the app-shell's Neue Plak — the
// attendee-surface rebrand. `styles/founders-grotesk.css` is imported FIRST
// (before the app-shell chrome paints) so its faces are ready; we do NOT import
// the app-shell `neue-plak.css`.
//
// Provider stack (matches the sibling chrome+data recipe):
//   QueryClientProvider (react-query v4 — the consumer-search EventCardSaveAction
//     + the app-shell GlobalHeader + the D4 follow hooks all need it)
//   > IntlProvider (react-intl + webapp-locales)
//     > FollowClientProvider (D4 @eventbrite/follow-organizer-ebui, a CSRF-echoing
//       fetcher against this app's own BFF at `/cc/api/v3`)
//       > <Component/>
//   + ConsentManagerScripts (webapp-data) paired with the Transcend <Script> in
//     _document (missing pair = window.airgap runtime crash).
//
// Dropped from the eb-ui bootstrap (faithful drops, documented per the config):
//   - initializeRealUserMonitoring (real-user-monitoring ghost)
//   - useAttributionClickTracking / clid-courier (ghost)
//   - displayFXSurveyIntercept (fxSurvey ghost — dead Usabilla intercept)
//   - FeatureFlagsProvider moved to the page (needs the per-request flag map from gSSP)
import '@/styles/founders-grotesk.css';
import '@/styles/cc.scss';
import '@eventbrite/webapp-fonts/fonts.css';

import { BspProvider } from '@eventbrite/bsp-sdk/react';
import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';
import { FavIconLinks, MetaTags } from '@eventbrite/webapp-public-content';
import {
    FollowClientProvider,
    FollowOrganizerClient,
} from '@eventbrite/follow-organizer-ebui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { AppContext, AppProps } from 'next/app';
import NextApp from 'next/app';
import Head from 'next/head';
import React, { useMemo } from 'react';
import { IntlProvider } from 'react-intl';

import { useBspConfig } from '@/lib/bsp-config';
import { setMessages } from '@/lib/gettext';
import { AnalyticsBridgeMount } from '@/ported/_shared/analytics';
import { formatLocale, getLocaleMessages } from '../../i18n/locale';

// Bugbot (High, fleet rule): the D4 default fetcher sends NO headers on the
// bodyless follow/unfollow POSTs, but this app's BFF (follow.page.ts /
// unfollow.page.ts) enforces double-submit CSRF (rejects unless X-CSRFToken
// matches the csrftoken cookie) — every real-mode follow/unfollow would 403.
// Same fix as the sibling recipe: a custom fetcher that echoes the csrftoken
// cookie as the X-CSRFToken header (a value a cross-site attacker cannot
// read/forge) on POSTs.
function readCsrfToken(): string {
    if (typeof document === 'undefined') {
        return '';
    }
    const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : '';
}

async function csrfFetcher(path: string, init: { method?: 'GET' | 'POST'; body?: string } = {}): Promise<unknown> {
    const method = init.method ?? 'GET';
    const headers: Record<string, string> = {};
    if (init.body) {
        headers['Content-Type'] = 'application/json';
    }
    if (method === 'POST') {
        headers['X-CSRFToken'] = readCsrfToken();
    }
    const response = await fetch(path, {
        method,
        credentials: 'include',
        headers: Object.keys(headers).length ? headers : undefined,
        body: init.body,
    });
    if (!response.ok) {
        throw new Error(`follow-organizer request failed: ${method} ${path} → ${response.status}`);
    }
    const text = await response.text();
    return text ? (JSON.parse(text) as unknown) : {};
}

interface CollectionsAppProps extends AppProps {
    stage: Stage;
    locale: string;
    messages: Record<string, string>;
}

export default function App({
    Component,
    pageProps,
    stage,
    locale,
    messages,
}: CollectionsAppProps) {
    const queryClient = useMemo(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        retry: false,
                        refetchOnWindowFocus: false,
                        refetchOnReconnect: false,
                        refetchOnMount: false,
                    },
                },
            }),
        [],
    );

    // D4 follow client — same-origin `/cc/api/v3` (basePath `/cc` + the BFF's
    // `/api/v3` routes), with the CSRF-echoing fetcher the BFF requires.
    const followClient = useMemo(
        () => new FollowOrganizerClient({ apiBase: '/cc/api/v3', fetcher: csrfFetcher }),
        [],
    );

    // Feed the gettext shim's active catalog before the tree renders
    // (idempotent, no state).
    setMessages(messages);

    const bspConfig = useBspConfig(stage);

    return (
        <>
            <Head>
                <FavIconLinks stage={stage} />
            </Head>
            <MetaTags showDefaultDescription={false} />
            <QueryClientProvider client={queryClient}>
                <IntlProvider locale={locale} defaultLocale="en-US" messages={messages}>
                    {/* ConsentManagerScripts is a sibling BEFORE BspProvider per
                        the bsp-sdk README's documented ordering (consent is
                        re-evaluated synchronously per-event with a fail-open
                        default if airgap isn't ready yet, so ordering is
                        convention, not correctness).
                        BspProvider restores the legacy `BspAppRoot` bootstrap
                        (eb-ui index.tsx): boots the PicoX SDK → auto `page_view`
                        on mount + `ping_web`, and makes `usePicoXTrack()` (the
                        real dispatch behind the app's `trackEvent` seam) live.
                        Consent is re-evaluated per-event by BspProvider against
                        window.airgap, so PicoX traffic respects the same
                        Transcend gate — it undercounts like Heap did; CloudWatch
                        stays the volume SSOT. `stage` arrives via
                        App.getInitialProps (module-level process.env.STAGE is
                        undefined in the browser — covid-19-webapp Bugbot PR #89). */}
                    <ConsentManagerScripts />
                    <BspProvider config={bspConfig}>
                        <AnalyticsBridgeMount />
                        <FollowClientProvider client={followClient}>
                            <Component {...pageProps} />
                        </FollowClientProvider>
                    </BspProvider>
                </IntlProvider>
            </QueryClientProvider>
        </>
    );
}

App.getInitialProps = async (context: AppContext) => {
    const appProps = await NextApp.getInitialProps(context);
    const stage = stageFromString(process.env.STAGE || Stage.TEST);
    const req = context.ctx.req;
    const localeUnderscore = req ? getLocaleFromRequest(req) : 'en_US';
    const messages = await getLocaleMessages(localeUnderscore);
    return {
        ...appProps,
        stage,
        locale: formatLocale(localeUnderscore),
        messages,
    };
};
